import {
  Product,
  ProductPurchaseInventory,
  ProductSimple,
  ProductWithVariants,
} from "@made-people/grease-product";

const toAllocatedInventory = (
  inventory: ProductPurchaseInventory,
  additionalInventory: ProductPurchaseInventory[],
  allocatedWarehouseIds: string[],
): ProductPurchaseInventory => {
  if (additionalInventory.length === 0) {
    return inventory;
  }

  const quantity = additionalInventory
    .filter((inv) => allocatedWarehouseIds.includes(inv.warehouseId))
    .reduce((total, inv) => total + inv.quantity, 0);

  return { ...inventory, quantity, inStock: quantity > 0 };
};

/**
 * Centra reports stock aggregated across every warehouse, but only the
 * warehouses in the market's allocation rule can actually be sold from. Rewrite
 * the inventory each consumer reads (size picker, add to cart, purchase status)
 * so it reflects allocated stock only.
 *
 * Returns the product untouched when the allocation rule is unavailable, or
 * when Centra sent no per-warehouse breakdown to filter by.
 */
export const applyAllocationRuleToProduct = (
  product: Product,
  allocatedWarehouseIds: string[] | undefined,
): Product => {
  if (!allocatedWarehouseIds || allocatedWarehouseIds.length === 0) {
    return product;
  }

  if (product.dataModel === "with_variants") {
    const withVariants = product as ProductWithVariants;
    return {
      ...withVariants,
      variants: withVariants.variants.map((variant) => ({
        ...variant,
        inventory: toAllocatedInventory(
          variant.inventory,
          variant.additionalInventory,
          allocatedWarehouseIds,
        ),
      })),
    };
  }

  if (product.dataModel === "simple") {
    const simple = product as ProductSimple;
    return {
      ...simple,
      inventory: toAllocatedInventory(
        simple.inventory,
        simple.additionalInventory,
        allocatedWarehouseIds,
      ),
    };
  }

  return product;
};
